/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import * as React from 'react';

import { type WebConfig, webConfig } from '@gitbook/env';
import { Logger } from '@gitbook/util-debug';
import { useHead } from '@gitbook/util-react-head';
import { useIsBrowserIdle } from '@gitbook/util-react-hooks';

import { useCurrentUser } from './auth';

const logger = Logger('app:customerio');

const CUSTOMER_IO_QUEUE_METHODS: CustomerIoQueuedMethod[] = [
    'load',
    'identify',
    'sidentify',
    'track',
    'page',
    'on',
    'off',
];

/**
 * Hook to enable customer.io In-App Messages
 */
export function useCustomerIoInAppMessages() {
    useInitCustomerIo();
    useInsertCustomerIoTrackingScript(webConfig.customerio);
}

/**
 * Load the customer.io tracking script
 */
function useInsertCustomerIoTrackingScript(customerIoEnv: WebConfig['customerio']) {
    const isIdle = useIsBrowserIdle();
    useHead((head) => {
        if (!customerIoEnv) {
            return;
        }

        if (isIdle && !process.env.GITBOOK_PREDICTABLE_UI) {
            const { siteId, orgId, region = 'US' } = customerIoEnv;
            head.writeAsync({
                type: 'script',
                id: 'cio-tracker',
                src:
                    region === 'US'
                        ? 'https://assets.customer.io/assets/track.js'
                        : 'https://assets.customer.io/assets/track-eu.js',
                dataset: {
                    'site-id': siteId,
                    'use-array-params': 'true',
                    'in-app-org-id': orgId,
                    'use-in-app': 'true',
                },
            });
        }
    });
}

/**
 * Initialize the customer.io JS client global instance
 */
function useInitCustomerIo() {
    const currentUser = useCurrentUser();

    React.useEffect(() => {
        window._cio = window._cio || [];

        if (Array.isArray(window._cio)) {
            const queue =
                (method: string): CustomerIoQueuedMethodFn =>
                (...args) => {
                    window._cio.push([method].concat(Array.prototype.slice.call(args, 0)));
                };
            for (const method of CUSTOMER_IO_QUEUE_METHODS) {
                window._cio[method] = queue(method);
            }
        }
    }, []);

    React.useEffect(() => {
        if (currentUser) {
            logger.info(
                `identify user as "${currentUser.uid}"${
                    currentUser.email ? ` (email: ${currentUser.email})` : ''
                } with customer.io`
            );
            const userCreatedAt = currentUser.metadata.creationTime
                ? Math.floor(new Date(currentUser.metadata.creationTime).getTime() / 1000)
                : undefined;
            window._cio.identify({
                id: currentUser.uid,
                email: currentUser.email,
                created_at: userCreatedAt,
            });
        }
    }, [currentUser]);
}
